import { useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ISO_COUNTRIES, FAVORITE_CODES, COUNTRY_NAME } from "@/lib/isoCountries";

/**
 * Shared searchable multi-select country control for citizenships[].
 * `value` is an array of canonical entries [{country_code, country_name, is_primary}].
 * `onChange(entries)` returns the updated list. Duplicate-prevented, keyboard accessible.
 * The FIRST entry (or the one flagged) is primary; a star toggles primary designation.
 */
export default function CitizenshipSelect({ value = [], onChange, testidPrefix = "citizenship", disabled = false }) {
  const [open, setOpen] = useState(false);
  const entries = Array.isArray(value) ? value : [];
  const selectedCodes = new Set(entries.map((e) => e.country_code));

  const options = useMemo(() => {
    const favs = FAVORITE_CODES.map((c) => ISO_COUNTRIES.find((x) => x.code === c)).filter(Boolean);
    const rest = ISO_COUNTRIES.filter((x) => !FAVORITE_CODES.includes(x.code));
    return { favs, rest };
  }, []);

  const emit = (next) => {
    // guarantee exactly one primary (without mutating the incoming array)
    const out = next.some((e) => e.is_primary)
      ? next
      : next.map((e, i) => (i === 0 ? { ...e, is_primary: true } : e));
    onChange?.(out);
  };

  const add = (code) => {
    if (selectedCodes.has(code)) return;
    emit([...entries, { country_code: code, country_name: COUNTRY_NAME[code] || code, is_primary: entries.length === 0 }]);
  };
  const remove = (code) => emit(entries.filter((e) => e.country_code !== code).map((e, i, arr) => ({ ...e, is_primary: arr.length === 1 ? true : e.is_primary })));
  const makePrimary = (code) => emit(entries.map((e) => ({ ...e, is_primary: e.country_code === code })));

  return (
    <div data-testid={`${testidPrefix}-select`}>
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testidPrefix}-chips`}>
        {entries.length === 0 && <span className="text-xs text-slate-400">No citizenships selected</span>}
        {entries.map((e) => (
          <span key={e.country_code} data-testid={`${testidPrefix}-chip-${e.country_code}`}
            className={`inline-flex items-center gap-1 rounded-full pl-2 pr-1 py-0.5 text-xs font-medium border ${e.is_primary ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
            {!disabled && (
              <button type="button" title={e.is_primary ? "Primary citizenship" : "Set as primary"} onClick={() => makePrimary(e.country_code)}
                data-testid={`${testidPrefix}-primary-${e.country_code}`} className="text-amber-500 hover:text-amber-600">
                <Icons.Star className={`h-3 w-3 ${e.is_primary ? "fill-amber-400" : ""}`} />
              </button>
            )}
            {e.country_name || COUNTRY_NAME[e.country_code] || e.country_code}
            {!disabled && (
              <button type="button" onClick={() => remove(e.country_code)} data-testid={`${testidPrefix}-remove-${e.country_code}`}
                className="ml-0.5 text-slate-400 hover:text-rose-500"><Icons.X className="h-3 w-3" /></button>
            )}
          </span>
        ))}
      </div>
      {!disabled && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" data-testid={`${testidPrefix}-add`}
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 border border-dashed border-blue-300 rounded-lg px-2.5 py-1.5 hover:bg-blue-50">
              <Icons.Plus className="h-3.5 w-3.5" /> Add country
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <Command>
              <CommandInput placeholder="Search country…" data-testid={`${testidPrefix}-search`} />
              <CommandList>
                <CommandEmpty>No country found.</CommandEmpty>
                <CommandGroup heading="Frequently selected">
                  {options.favs.filter((o) => !selectedCodes.has(o.code)).map((o) => (
                    <CommandItem key={o.code} value={`${o.name} ${o.code}`} onSelect={() => { add(o.code); setOpen(false); }}
                      data-testid={`${testidPrefix}-opt-${o.code}`}>{o.name}</CommandItem>
                  ))}
                </CommandGroup>
                <CommandGroup heading="All countries">
                  {options.rest.filter((o) => !selectedCodes.has(o.code)).map((o) => (
                    <CommandItem key={o.code} value={`${o.name} ${o.code}`} onSelect={() => { add(o.code); setOpen(false); }}
                      data-testid={`${testidPrefix}-opt-${o.code}`}>{o.name}</CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
      <p className="text-[11px] text-slate-400 mt-1">Select every country where the person currently holds citizenship.</p>
    </div>
  );
}

/** Read-only badge display of citizenships (Contact / Lead / Client detail surfaces). */
export function CitizenshipBadges({ value = [], testid = "citizenship-badges" }) {
  const entries = Array.isArray(value) ? value : [];
  if (entries.length === 0) return <span className="text-slate-300">—</span>;
  return (
    <span className="inline-flex flex-wrap gap-1" data-testid={testid}>
      {entries.map((e) => (
        <span key={e.country_code} data-testid={`${testid}-${e.country_code}`}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${e.is_primary ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
          {e.is_primary && <Icons.Star className="h-2.5 w-2.5 fill-amber-400 text-amber-500" />}
          {e.country_name || COUNTRY_NAME[e.country_code] || e.country_code}
        </span>
      ))}
    </span>
  );
}
